import type { IGetPaymentTypesResultData } from "../services/model/pos.api.model";
import { TenderType } from "../services/model/tender-type.enum";

export type PaymentOperation = "sale" | "return";
export type PaymentDocumentType = "trip" | "fly-ticket" | "e-travel" | "voucher-gift" | "voucher-benefit";
export type PaymentAvailability = Partial<Record<PaymentOperation, Partial<Record<PaymentDocumentType, boolean>>>>;

export interface PaymentContext {
  operation: PaymentOperation;
  documentType: PaymentDocumentType;
  isCrossSale?: boolean;
  // DEV-14262: share the raw balance so the page and dialog recognize the same refund exception.
  remainingToPay?: number;
}

export type PaymentGroup = "cash" | "card" | "benefits" | "other" | "voucher";

// Use one payment identity for availability and amount validation; other tenders can share the same type.
export const SKODA_PAYMENT_NAME = "POUK SKODA";

export function isTripReturnWithoutOverpayment(context: PaymentContext | undefined): boolean {
  // DEV-14262: a nonnegative amount due means zero refundable balance or an underpayment.
  // Require a loaded, finite balance and a trip return so other agendas retain their existing rules.
  return context?.operation === "return"
    && context.documentType === "trip"
    && Number.isFinite(context.remainingToPay)
    && context.remainingToPay >= 0;
}

export function isPaymentAvailable(payment: IGetPaymentTypesResultData, context: PaymentContext): boolean {
  // Existing database availability checks remain unchanged; DEV-14262 adds only the balance restriction below.
  return payment?.IsPayAllowed === true
    && isPaymentAvailabilityValid(payment.Availability)
    && payment.Availability?.[context.operation]?.[context.documentType] === true
    && !(context.isCrossSale && payment.IsBenefit === true)
    // DEV-14262: exclude OC vouchers both from the initial offer and when confirming an older allowed-payment list.
    && (!isTripReturnWithoutOverpayment(context) || payment.Name === SKODA_PAYMENT_NAME);
}

export function isPaymentAvailabilityValid(value: unknown): value is PaymentAvailability {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  return Object.entries(value).every(([operation, documents]) =>
    ["sale", "return"].includes(operation)
    && documents !== null && typeof documents === "object" && !Array.isArray(documents)
    && Object.entries(documents).every(([document, allowed]) =>
      ["trip", "fly-ticket", "e-travel", ...(operation === "sale" ? ["voucher-gift", "voucher-benefit"] : [])].includes(document)
      && typeof allowed === "boolean"));
}

export function isPaymentInGroup(payment: IGetPaymentTypesResultData, group: PaymentGroup): boolean {
  switch (group) {
    case "cash": return payment.TenderType === TenderType.Cash;
    case "card": return payment.TenderType === TenderType.Card;
    // The standalone "Použít poukaz" action is the gift-voucher flow.
    // Benefit vouchers are selected in the Benefits dialog and must not make
    // this button visible merely because they use the same tender type.
    case "voucher": return payment.TenderType === TenderType.Voucher && payment.IsBenefit !== true;
    case "other": return payment.IncludedItemAssortmentCode === "OTHERS";
    case "benefits": return payment.IsBenefit === true
      && [TenderType.ElectronicVoucher, TenderType.Cheque, TenderType.Virtual].includes(payment.TenderType);
    default: return false;
  }
}
